import React from "react";
import SEO from "../components/SEO";
import "../styles/horse.css";

function Horse() {
  const whatsappNumber = "919983765215";

  const bookingMessage = encodeURIComponent(
    "Hello, I want to book horse riding at Pratap Country in Titardi Village, Udaipur for 1 hour (₹2500). Please share availability and booking details."
  );

  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${bookingMessage}`;

  /* =====================================================
     BREADCRUMBS
  ====================================================== */

  const breadcrumbs = [
    {
      name: "Home",
      url: "https://vkbikeonrent.in/",
    },
    {
      name: "Horse Riding",
      url: "https://vkbikeonrent.in/horse-riding",
    },
  ];

  /* =====================================================
     FAQ CONTENT
  ====================================================== */

  const faqs = [
    {
      question: "Where can I go horse riding in Udaipur?",
      answer:
        "VK Bike On Rent currently offers this horse riding experience at Pratap Country in Titardi Village, Udaipur. Contact us on WhatsApp to confirm current availability.",
    },
    {
      question: "How long is the horse riding session?",
      answer:
        "The listed horse riding session is approximately 1 hour.",
    },
    {
      question: "How much does horse riding in Titardi Village cost?",
      answer:
        "The current listed price is ₹2500 for a 1-hour session. Please confirm the price and availability before booking.",
    },
    {
      question: "How can I book horse riding in Titardi Village?",
      answer:
        "You can contact VK Bike On Rent through WhatsApp to check availability and request booking details.",
    },
  ];

  /* =====================================================
     FAQ SCHEMA
  ====================================================== */

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      {/* =====================================================
          PAGE SEO
      ====================================================== */}

      <SEO
        title="Horse Riding in Udaipur | Titardi Village | VK Bike On Rent"
        description="Horse riding in Titardi Village, Udaipur at Pratap Country. 1-hour horse riding experience listed at ₹2500. Contact VK Bike On Rent on WhatsApp to check availability."
        canonical="https://vkbikeonrent.in/horse-riding"
        breadcrumbs={breadcrumbs}
        schema={faqSchema}
      />

      {/* =====================================================
          PAGE
      ====================================================== */}

      <main className="horse-page">

        {/* =================================================
            PAGE INTRODUCTION
        ================================================== */}

        <section
          className="section"
          aria-labelledby="horse-page-title"
        >
          <h1
            id="horse-page-title"
            className="horse-title"
          >
            Horse Riding in Titardi Village, Udaipur
          </h1>

          <p className="horse-intro">
            Enjoy a horse riding experience in Titardi Village
            near Udaipur. Contact VK Bike On Rent to check
            availability and book your riding session through
            WhatsApp.
          </p>
        </section>

        {/* =================================================
            HORSE RIDING SERVICE
        ================================================== */}

        <section
          className="horse-card"
          aria-labelledby="horse-service-title"
        >

          <div className="horse-image-wrapper">

            <img
              src="https://images.unsplash.com/photo-1553284965-83fd3e82fa5a"
              alt="Horse riding experience in Titardi Village near Udaipur"
              className="horse-image"
              width="1200"
              height="800"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />

          </div>

          <div className="horse-details">

            <h2
              id="horse-service-title"
              className="horse-service-title"
            >
              Horse Riding at Pratap Country
            </h2>

            <div className="horse-information">

              <p className="horse-info">
                <span aria-hidden="true">
                  📍
                </span>{" "}
                <strong>Location:</strong>{" "}
                Titardi Village, Udaipur
              </p>

              <p className="horse-info">
                <span aria-hidden="true">
                  ⏱
                </span>{" "}
                <strong>Duration:</strong>{" "}
                1 hour
              </p>

              <p className="horse-price">
                <strong>₹2500</strong>{" "}
                <span>for 1 hour</span>
              </p>

            </div>

            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="horse-book-btn"
              aria-label="Book horse riding in Titardi Village on WhatsApp"
            >
              📲 Book Horse Riding on WhatsApp
            </a>

          </div>

        </section>

        {/* =================================================
            EXPERIENCE INFORMATION
        ================================================== */}

        <section
          className="horse-seo-text"
          aria-labelledby="horse-experience-title"
        >

          <h2 id="horse-experience-title">
            Horse Riding Experience in Titardi Village
          </h2>

          <p>
            Looking for horse riding in Udaipur? Titardi
            Village offers an opportunity to enjoy an outdoor
            horse riding experience away from the busy city
            areas. The experience is suitable for visitors who
            want to add an outdoor activity to their Udaipur
            trip.
          </p>

          <p>
            The horse riding session currently offered through
            VK Bike On Rent is approximately{" "}
            <strong>1 hour</strong> and is listed at{" "}
            <strong>₹2500</strong>. Availability may vary,
            so we recommend contacting us on WhatsApp before
            visiting.
          </p>

        </section>

        {/* =================================================
            HORSE RIDING DETAILS
        ================================================== */}

        <section
          className="horse-details-section"
          aria-labelledby="horse-details-title"
        >

          <h2 id="horse-details-title">
            Horse Riding Details
          </h2>

          <ul>
            <li>
              📍 Location:{" "}
              <strong>
                Titardi Village, Udaipur
              </strong>
            </li>

            <li>
              ⏱ Duration:{" "}
              <strong>
                1 hour
              </strong>
            </li>

            <li>
              💰 Price:{" "}
              <strong>
                ₹2500
              </strong>
            </li>

            <li>
              📲 Booking:{" "}
              <strong>
                WhatsApp
              </strong>
            </li>
          </ul>

        </section>

        {/* =================================================
            WHO CAN CONSIDER THIS EXPERIENCE
        ================================================== */}

        <section
          className="horse-audience-section"
          aria-labelledby="horse-audience-title"
        >

          <h2 id="horse-audience-title">
            Who Can Consider This Experience?
          </h2>

          <p>
            Horse riding in Titardi Village can be considered
            by tourists, couples, families and visitors looking
            for an outdoor activity during their stay in
            Udaipur.
          </p>

          <p>
            Before booking, contact us to confirm availability
            and any age, weight, riding or other requirements.
          </p>

        </section>

        {/* =================================================
            FAQ / AEO
        ================================================== */}

        <section
          className="horse-faq"
          aria-labelledby="horse-faq-title"
        >

          <h2 id="horse-faq-title">
            Horse Riding in Titardi Village – FAQs
          </h2>

          {faqs.map((faq) => (
            <article
              className="horse-faq-item"
              key={faq.question}
            >

              <h3>
                {faq.question}
              </h3>

              <p>
                {faq.answer}
              </p>

            </article>
          ))}

        </section>

        {/* =================================================
            FINAL CTA
        ================================================== */}

        <section
          className="horse-booking-cta"
          aria-labelledby="horse-booking-title"
        >

          <h2 id="horse-booking-title">
            Book Horse Riding in Udaipur
          </h2>

          <p>
            Planning an outdoor activity in Udaipur?
            Contact VK Bike On Rent on WhatsApp to check
            availability for the Titardi Village horse riding
            experience.
          </p>

          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="horse-book-btn"
            aria-label="Check horse riding availability on WhatsApp"
          >
            Check Availability on WhatsApp
          </a>

        </section>

      </main>
    </>
  );
}

export default Horse;